import { Box, Button, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Offer } from "../../api/AlqemiDataService";
import SearchBar from "../../common/components/layout/SearchBar";
import { ItemRow } from "../../common/components/table/Row";
import Table from "../../common/components/table/Table";
import { RatingScoreBox } from "../../common/styles/Box.styles";
import { iconGap, smallGap, tinyGap } from "../../styles/consts";
import { formatDateFromDatabase } from "../../utils";
import MatchStatus from "../components/MatchStatus";
import { useAlqemiDashboard } from "../context/AlqemiDashboardProvider";
import CandidateFilterDropdown from "./CandidateFilterDropdown";
import CandidatesList from "./CandidatesList";
import EmailJobDialog from "./EmailJobDialog";

const saveSortName = "offersSorting";
const savePageNum = "alqemiOffersPage";
const saveFilterName = "alqemiOffersFilter";
export default function AlqemiDashboard({ basePath }) {
  const navigate = useNavigate();
  const {
    offers,
    candidates,
    currentFilters,
    addAlqemiFilter,
    removeAlqemiFilter,
    loadingData,
  } = useAlqemiDashboard();
  const previouslySavedSorting = localStorage.getItem(saveSortName);
  const defaultSorting = previouslySavedSorting
    ? JSON.parse(previouslySavedSorting)
    : {
        param: "created_at",
        order: "desc",
      };
  const [searchKey, setSearchKey] = useState(0);
  const savedPage = localStorage.getItem(savePageNum) || "1";
  const [alqemiPageNum, setAlqemiPageNum] = useState(
    savedPage ? parseInt(savedPage) : 1,
  );
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [expandedInactiveOffers, setExpandedInactiveOffers] = useState<
    Set<string>
  >(new Set());

  const toggleInactiveExpanded = useCallback((offerId: string) => {
    setExpandedInactiveOffers((prev) => {
      const next = new Set(prev);
      if (next.has(offerId)) {
        next.delete(offerId);
      } else {
        next.add(offerId);
      }
      return next;
    });
  }, []);

  const handleGoToOffer = async (offer: Offer, candidateEmail?: string) => {
    const currentCandidateFilter = currentFilters.find(
      (f) => f.field === "currentCandidate",
    );
    let url;
    if (candidateEmail) {
      url = `${basePath}/${offer.id}?currentCandidate=${encodeURIComponent(candidateEmail)}`;
    } else if (currentCandidateFilter) {
      const candidateId = currentCandidateFilter.value;
      const candidate = candidates.find((c) => c.consultant_id === candidateId);
      if (candidate && candidate.email) {
        url = `${basePath}/${offer.id}?currentCandidate=${encodeURIComponent(candidate.email)}`;
      } else {
        url = `${basePath}/${offer.id}`;
      }
    } else {
      url = `${basePath}/${offer.id}`;
    }
    navigate(url);
  };

  const getOfferRow = (
    offer: Offer,
    consultantIdFilter?: string,
  ): ItemRow[] => {
    function ClickableStack({
      children,
      candidateEmail,
    }: {
      children: JSX.Element[] | JSX.Element;
      candidateEmail?: string;
    }) {
      const offerUrl = candidateEmail
        ? `${basePath}/${offer.id}?currentCandidate=${encodeURIComponent(candidateEmail)}`
        : `${basePath}/${offer.id}`;

      return (
        <a
          href={offerUrl}
          onClick={(e) => {
            e.preventDefault();
            handleGoToOffer(offer, candidateEmail);
          }}
          target="_self"
          rel="noopener noreferrer"
          style={{
            textDecoration: "none",
            color: "inherit",
            display: "flex",
            width: "100%",
          }}
        >
          <Stack
            sx={{
              width: "100%",
              height: "100%",
              cursor: "crosshair",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {children}
          </Stack>
        </a>
      );
    }

    const isEmailSourced = offer.provider === "email";
    const isInactiveExpanded = expandedInactiveOffers.has(offer.id);

    return [
      {
        sortParam: "title",
        label: "Offer",
        component: (
          <ClickableStack>
            <Stack
              sx={{
                flexDirection: "row",
                alignItems: "center",
                gap: tinyGap,
                flexWrap: "wrap",
              }}
            >
              <Typography>{offer.title || "Offer title"}</Typography>
              {isEmailSourced && (
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: "#ff5950",
                    border: "1px solid #ff5950",
                    borderRadius: "10px",
                    padding: "1px 8px",
                    lineHeight: 1.4,
                    whiteSpace: "nowrap",
                  }}
                >
                  Recruiter email
                </Box>
              )}
            </Stack>
          </ClickableStack>
        ),
        width: "35%",
        minWidth: "7rem",
        filterConfig: {
          type: "text",
        },
      },
      {
        sortParam: "status",
        label: "Status",
        component: (
          <ClickableStack>
            <Stack sx={{ gap: iconGap }}>
              {offer.matches && offer.matches.length > 0 ? (
                consultantIdFilter &&
                consultantIdFilter !== "All candidates" ? (
                  (() => {
                    const match = offer.matches.find(
                      (m) => m.consultant_id === consultantIdFilter,
                    );
                    if (!match) return null;

                    // "sent", "rejected", "timed_out" are terminal statuses that should not be overridden
                    let displayStatus = match.status;
                    if (
                      match.status !== "sent" &&
                      match.status !== "rejected" &&
                      match.status !== "timed_out"
                    ) {
                      if (match.supervisor_confirmed) {
                        displayStatus = "approved";
                      } else if (match.consultant_confirmed) {
                        displayStatus =
                          match.supervisor_enabled_at_confirmation === false
                            ? "approved"
                            : "supervisor_review";
                      }
                    }
                    // Handle legacy "viewed" status
                    if (displayStatus === "viewed") {
                      displayStatus = "consultant_review";
                    }

                    return (
                      <MatchStatus
                        key={match.consultant_id}
                        status={displayStatus}
                      />
                    );
                  })()
                ) : (
                  (() => {
                    if (!offer.sortedMatches) {
                      offer.sortedMatches = [...offer.matches].sort((a, b) => {
                        const findCandidate = (match) =>
                          candidates.find(
                            (c) => c.consultant_id === match.consultant_id,
                          ) || { name: "", email: "" };

                        const candidateA = findCandidate(a);
                        const candidateB = findCandidate(b);

                        return candidateA.name.localeCompare(candidateB.name);
                      });
                    }

                    const renderMatchStatus = (
                      match,
                      key: string,
                      isInactiveRow: boolean,
                    ) => {
                      let displayStatus = match.status;
                      if (
                        match.status !== "sent" &&
                        match.status !== "rejected" &&
                        match.status !== "timed_out"
                      ) {
                        if (match.supervisor_confirmed) {
                          displayStatus = "approved";
                        } else if (match.consultant_confirmed) {
                          displayStatus =
                            match.supervisor_enabled_at_confirmation === false
                              ? "approved"
                              : "supervisor_review";
                        }
                      }
                      if (displayStatus === "viewed") {
                        displayStatus = "consultant_review";
                      }
                      return (
                        <Box
                          key={key}
                          sx={isInactiveRow ? { opacity: 0.45 } : undefined}
                        >
                          <MatchStatus status={displayStatus} />
                        </Box>
                      );
                    };

                    const activeMatches = offer.sortedMatches.filter(
                      (m) => m.consultant_active !== false,
                    );
                    const inactiveMatches = offer.sortedMatches.filter(
                      (m) => m.consultant_active === false,
                    );

                    return (
                      <>
                        {activeMatches.map((m, i) =>
                          renderMatchStatus(m, `a-${i}`, false),
                        )}
                        {isInactiveExpanded &&
                          inactiveMatches.map((m, i) =>
                            renderMatchStatus(m, `i-${i}`, true),
                          )}
                        {inactiveMatches.length > 0 && (
                          <Box
                            component="span"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleInactiveExpanded(offer.id);
                            }}
                            sx={{
                              fontSize: "0.78rem",
                              color: "#666",
                              cursor: "crosshair",
                              textDecoration: "underline",
                              userSelect: "none",
                              alignSelf: "flex-start",
                              mt: 0.5,
                            }}
                          >
                            {isInactiveExpanded
                              ? `Hide ${inactiveMatches.length} inactive`
                              : `Show ${inactiveMatches.length} inactive`}
                          </Box>
                        )}
                      </>
                    );
                  })()
                )
              ) : (
                <MatchStatus status={"pending"} />
              )}
            </Stack>
          </ClickableStack>
        ),
        width: "15%",
        minWidth: "6rem",
        filterConfig: {
          type: "select",
          options: [
            { value: "matched", name: "Matched" },
            { value: "filtered", name: "Filtered" },
            { value: "consultant_review", name: "Consultant review" },
            { value: "supervisor_review", name: "Supervisor review" },
            { value: "approved", name: "Approved" },
            { value: "sent", name: "Sent" },
            { value: "rejected", name: "Rejected" },
            { value: "timed_out", name: "Timed out" },
          ],
        },
      },
      {
        sortParam: "matches.score",
        label: "Candidate's rating",
        component: (
          <Stack sx={{ gap: tinyGap, width: "100%" }}>
            {offer.matches &&
              (consultantIdFilter && consultantIdFilter !== "All candidates"
                ? (() => {
                    const match = offer.matches.find(
                      (m) => m.consultant_id === consultantIdFilter,
                    );
                    if (!match) return null;

                    const candidate = candidates.find(
                      (c) => c.consultant_id === match.consultant_id,
                    ) || { name: "", email: "" };

                    const responsiveWidthStyles = {
                      "@media (min-width: 1000px) and (max-width: 1300px)": {
                        width: "70%",
                      },
                      "@media (min-width: 1300px) and (max-width: 2300px)": {
                        width: "60%",
                      },
                      "@media (min-width: 2300px)": {
                        width: "40%",
                      },
                    };

                    return (
                      <ClickableStack candidateEmail={candidate.email}>
                        <Stack
                          sx={{
                            flexDirection: "row",
                            ...responsiveWidthStyles,
                            maxGap: tinyGap,
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography>{candidate.name}</Typography>
                          <RatingScoreBox>{match.score}</RatingScoreBox>
                        </Stack>
                      </ClickableStack>
                    );
                  })()
                : (() => {
                    const resolveCandidate = (match) => {
                      const fromCandidates = candidates.find(
                        (c) => c.consultant_id === match.consultant_id,
                      );
                      const name =
                        fromCandidates?.name ||
                        match.consultant_name ||
                        "(unknown)";
                      const email =
                        fromCandidates?.email || match.consultant_email || "";
                      return { name, email };
                    };

                    const sortedMatches = [...offer.matches].sort((a, b) => {
                      const candidateA = resolveCandidate(a);
                      const candidateB = resolveCandidate(b);
                      return candidateA.name.localeCompare(candidateB.name);
                    });
                    offer.sortedMatches = sortedMatches;

                    const responsiveWidthStyles = {
                      "@media (min-width: 1000px) and (max-width: 1300px)": {
                        width: "70%",
                      },
                      "@media (min-width: 1300px) and (max-width: 2300px)": {
                        width: "60%",
                      },
                      "@media (min-width: 2300px)": {
                        width: "40%",
                      },
                    };

                    const renderCandidateRow = (
                      match,
                      key: string,
                      isInactiveRow: boolean,
                    ) => {
                      const candidate = resolveCandidate(match);
                      return (
                        <ClickableStack
                          key={key}
                          candidateEmail={candidate.email}
                        >
                          <Stack
                            sx={{
                              flexDirection: "row",
                              ...responsiveWidthStyles,
                              maxGap: tinyGap,
                              justifyContent: "space-between",
                              opacity: isInactiveRow ? 0.45 : 1,
                            }}
                          >
                            <Typography>{candidate.name}</Typography>
                            <RatingScoreBox>{match.score}</RatingScoreBox>
                          </Stack>
                        </ClickableStack>
                      );
                    };

                    const activeMatches = sortedMatches.filter(
                      (m) => m.consultant_active !== false,
                    );
                    const inactiveMatches = sortedMatches.filter(
                      (m) => m.consultant_active === false,
                    );

                    return (
                      <>
                        {activeMatches.map((m, i) =>
                          renderCandidateRow(m, `a-${i}`, false),
                        )}
                        {isInactiveExpanded &&
                          inactiveMatches.map((m, i) =>
                            renderCandidateRow(m, `i-${i}`, true),
                          )}
                      </>
                    );
                  })())}
          </Stack>
        ),
        width: "25%",
        minWidth: "6rem",
        filterConfig: {
          includeGlobalFilter: true,
          type: "number",
          min: 0,
          max: 10,
        },
      },
      {
        sortParam: "created_at",
        label: "Date scraped",
        component: (
          <ClickableStack>
            <Typography>
              {formatDateFromDatabase(offer.created_at, true)}
            </Typography>
          </ClickableStack>
        ),
        width: "20%",
        minWidth: "6rem",
        filterConfig: {
          type: "date",
        },
      },
    ];
  };

  return (
    <Stack
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <SearchBar
        type="offers"
        onSearch={async (searchQuery) => {
          if (searchQuery) {
            addAlqemiFilter({ field: "searchQuery", value: searchQuery });
          } else {
            removeAlqemiFilter("searchQuery");
          }
          setSearchKey((prev) => prev + 1);
        }}
        setLoadingData={() => {}}
      />
      <Table
        key={searchKey}
        filters={[]}
        hideDropdown={true}
        items={offers}
        idProperty="url"
        deleteAllFunction={() => {}}
        filterFunction={() => offers}
        getItemRow={getOfferRow}
        defaultSort={defaultSorting}
        saveSortName={saveSortName}
        savePageNum={savePageNum}
        loading={loadingData}
        saveFilterName={saveFilterName}
        saveActiveFiltersName="alqemiOffersActiveFilters"
        upperStateForPage={alqemiPageNum}
        setUpperStateForPage={setAlqemiPageNum}
        filteringArea={
          <Stack
            sx={{
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Stack sx={{ flexDirection: "row", gap: smallGap }}>
              <CandidatesList />

              <Button
                variant="outlined"
                id={"top-offers-button"}
                onClick={async () => {
                  setSearchKey((prev) => prev + 1);
                  const showOnlyTopOffers = currentFilters.find(
                    (f) => f.field === "showOnlyTopOffers",
                  );
                  if (showOnlyTopOffers === undefined) {
                    addAlqemiFilter({
                      field: "showOnlyTopOffers",
                      value: false,
                    });
                    setAlqemiPageNum(1);
                  } else if (showOnlyTopOffers.value === false) {
                    removeAlqemiFilter("showOnlyTopOffers");
                    setAlqemiPageNum(1);
                  }
                }}
              >
                {(() => {
                  const filter = currentFilters.find(
                    (f) => f.field === "showOnlyTopOffers",
                  );
                  const isShowingAll = filter && filter.value === false;
                  return isShowingAll
                    ? "Show only top matches"
                    : "Show all matches";
                })()}
              </Button>

              <Button
                variant="primary"
                onClick={() => setEmailDialogOpen(true)}
                sx={{ ml: 1 }}
              >
                Import Project
              </Button>
            </Stack>

            <CandidateFilterDropdown
              candidates={candidates}
              setPageNum={setAlqemiPageNum}
              saveFilterName={saveFilterName}
            />
          </Stack>
        }
      />
      <EmailJobDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        onSuccess={(jobId) => {}}
      />
    </Stack>
  );
}
